/* Shopify renders every price and cart line; this controller only manages UI and requests. */
(function () {
  'use strict';

  class RoyalMiniCart {
    constructor(root) {
      this.root = root;
      this.rail = root.querySelector('#rc-mc-rail');
      this.content = root.querySelector('[data-rc-mc-content]');
      this.dialog = root.querySelector('[data-rc-mc-dialog]');
      this.drawerContent = root.querySelector('[data-rc-mc-drawer-content]');
      this.sectionId = root.dataset.sectionId;
      this.cartUrl = root.dataset.cartUrl;
      this.changeUrl = root.dataset.changeUrl;
      this.pending = Promise.resolve();
      this.busy = 0;
      this.syncFailed = false;
      this.enabled = true;
      root.addEventListener('click', event => this.click(event));
      root.addEventListener('change', event => {
        if (!event.target.matches('[data-rc-mc-quantity]')) return;
        const input = event.target;
        const row = input.closest('[data-rc-mc-item]');
        const quantity = Number(input.value);
        if (!Number.isInteger(quantity) || !input.checkValidity()) {
          input.reportValidity();
          input.value = row.dataset.quantity;
          return;
        }
        this.change(row, quantity, 'quantity');
      });
      root.addEventListener('submit', event => {
        if (this.busy || this.syncFailed) event.preventDefault();
      });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDrawer();
      });
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('rc-mini-cart-modal');
        const trigger = this.drawerTrigger && this.drawerTrigger.isConnected ? this.drawerTrigger : this.rail.querySelector('[data-rc-mc-review]');
        if (trigger) trigger.focus({ preventScroll: true });
      });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.dialog.open && root.classList.contains('is-open')) this.setOpen(false, true);
      });
      document.addEventListener('click', event => {
        const link = event.target.closest('.site-header-cart--button');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.returnFocus = link;
        this.setOpen(true, true);
        this.refresh();
      });
      document.addEventListener('product-form:cart:navigate', event => {
        if (root.dataset.cartRedirection === 'true') return;
        event.preventDefault();
        this.cartAdded(event.detail.cart);
      });
      window.addEventListener('cartcount:update', () => { if (!this.publishing) this.refresh(); });
      window.addEventListener('pageshow', event => { if (event.persisted) this.refresh(); });
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.refresh(); });
      root.classList.add('is-ready');
      let closed = false;
      try { closed = sessionStorage.getItem('rc-mini-cart-closed') === 'true'; } catch (_) { /* Storage is optional. */ }
      this.setOpen(Number(root.dataset.count) > 0 && !closed && window.matchMedia('(min-width: 720px)').matches);
    }

    setOpen(open, focus = false) {
      this.root.classList.toggle('is-open', open);
      document.body.classList.toggle('rc-mini-cart-open', open);
      this.rail.hidden = !open;
      document.querySelectorAll('.site-header-cart--button').forEach(link => {
        link.setAttribute('aria-controls', 'rc-mc-rail');
        link.setAttribute('aria-expanded', String(open));
      });
      try { sessionStorage.setItem('rc-mini-cart-closed', String(!open)); } catch (_) { /* Storage is optional. */ }
      if (focus) {
        const target = open ? this.rail.querySelector('[data-rc-mc-close]') : this.returnFocus || document.querySelector('.site-header-cart--button');
        if (target && target.isConnected) target.focus({ preventScroll: true });
      }
    }

    click(event) {
      const target = event.target.closest('button, a');
      if (!target) return;
      if (target.hasAttribute('data-rc-mc-close')) {
        if (this.dialog.open) this.closeDrawer();
        else this.setOpen(false, true);
      } else if (target.hasAttribute('data-rc-mc-review')) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        this.drawerTrigger = target;
        this.renderDrawer();
        this.dialog.showModal();
        document.body.classList.add('rc-mini-cart-modal');
        this.dialog.querySelector('[data-rc-mc-close]').focus();
      } else if (target.dataset.rcMcAction && !target.disabled) {
        const row = target.closest('[data-rc-mc-item]');
        const action = target.dataset.rcMcAction;
        const quantity = action === 'remove' ? 0 : Number(row.dataset.quantity) + (action === 'increase' ? 1 : -1) * Number(row.dataset.step);
        this.change(row, quantity, action);
      }
    }

    closeDrawer() { this.dialog.close(); }

    renderDrawer() {
      this.drawerContent.innerHTML = this.content.innerHTML;
      const link = this.drawerContent.querySelector('[data-rc-mc-review]');
      link.removeAttribute('data-rc-mc-review');
      link.textContent = 'View full cart';
      const summary = this.drawerContent.querySelector('.rc-mc-summary');
      const close = summary.querySelector('[data-rc-mc-close]');
      close.setAttribute('aria-label', 'Close cart');
      const header = document.createElement('div');
      header.className = 'rc-mc-drawer-header';
      const heading = document.createElement('h2');
      heading.textContent = `Your cart (${this.root.dataset.count})`;
      header.append(heading, close);
      this.drawerContent.prepend(header);
      const note = document.createElement('p');
      note.className = 'rc-mc-shipping-note';
      note.textContent = 'Shipping and taxes calculated at checkout.';
      summary.querySelector('.rc-mc-subtotal').after(note);
      summary.appendChild(summary.querySelector('.rc-mc-checkout-form'));
      this.drawerContent.appendChild(summary);
      this.drawerContent.appendChild(this.rail.querySelector('[data-rc-mc-error]').cloneNode(true));
      this.setBusy();
    }

    setBusy() {
      const disabled = this.busy > 0 || this.syncFailed;
      this.root.setAttribute('aria-busy', String(this.busy > 0));
      this.root.querySelectorAll('[data-rc-mc-action], [data-rc-mc-quantity], [name="checkout"]').forEach(control => {
        if (disabled) {
          if (!control.hasAttribute('data-rc-mc-was-disabled')) control.setAttribute('data-rc-mc-was-disabled', String(control.disabled && !control.hasAttribute('data-rc-warehouse-disabled')));
          control.disabled = true;
        } else if (control.hasAttribute('data-rc-mc-was-disabled')) {
          control.disabled = control.hasAttribute('data-rc-warehouse-disabled') || control.getAttribute('data-rc-mc-was-disabled') === 'true';
          control.removeAttribute('data-rc-mc-was-disabled');
        }
      });
    }

    enqueue(work) {
      this.busy += 1;
      this.setBusy();
      const operation = this.pending.then(work).catch(error => {
        this.showError(error.message || 'Your cart could not be updated. Please review your cart.');
      }).finally(() => {
        this.busy -= 1;
        this.setBusy();
      });
      this.pending = operation;
      return operation;
    }

    async request(url, options = {}) {
      const abort = new AbortController();
      const timeout = window.setTimeout(() => abort.abort(), 15000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: abort.signal });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Your cart could not be updated. Please review your cart.');
        return data;
      } catch (error) {
        if (error.name === 'AbortError') throw new Error('Your cart is taking longer than expected. Please review your cart.');
        throw error;
      } finally { window.clearTimeout(timeout); }
    }

    async read() {
      const url = new URL(this.cartUrl, window.location.origin);
      url.searchParams.set('sections', this.sectionId);
      const sections = await this.request(url.toString());
      this.render(sections[this.sectionId]);
    }

    refresh() {
      return this.enqueue(async () => {
        try { await this.read(); }
        catch (error) { this.syncFailed = true; throw error; }
      });
    }

    cartAdded(cart) {
      this.setOpen(true);
      if (cart) this.publishCount(cart.item_count);
      return this.refresh();
    }

    change(row, quantity, action) {
      if (this.busy || this.syncFailed || !Number.isInteger(quantity) || quantity < 0) return this.pending;
      const previous = Number(row.dataset.quantity);
      const min = Number(row.dataset.min);
      const step = Number(row.dataset.step);
      const max = row.dataset.max ? Number(row.dataset.max) : Infinity;
      if (quantity !== 0 && (quantity < min || quantity > max || (quantity - min) % step !== 0)) return this.pending;
      if (quantity === previous) return this.pending;
      const key = row.dataset.key;
      const inDrawer = this.dialog.open;
      const restoreFocus = this.root.contains(document.activeElement);
      return this.enqueue(async () => {
        let failure;
        try {
          const cart = await this.request(`${this.changeUrl}.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity, sections: [this.sectionId], sections_url: this.cartUrl }),
          });
          this.publishCount(cart.item_count);
          if (cart.sections && cart.sections[this.sectionId]) this.render(cart.sections[this.sectionId]);
          else await this.read();
          this.root.querySelector('[data-rc-mc-status]').textContent = quantity === 0 ? 'Item removed from cart.' : 'Cart quantity updated.';
        } catch (error) {
          failure = error;
          // A failed response may follow a successful write. Re-read, never retry the mutation.
          try { await this.read(); } catch (_) { this.syncFailed = true; }
        }
        if (failure) this.showError(failure.message);
        const scope = inDrawer ? this.drawerContent : this.content;
        const replacement = Array.from(scope.querySelectorAll('[data-rc-mc-item]')).find(item => item.dataset.key === key);
        const focus = replacement && replacement.querySelector(action === 'quantity' ? '[data-rc-mc-quantity]' : `[data-rc-mc-action="${action}"]`);
        // Wait until the queue re-enables controls before restoring keyboard focus.
        this.restoreFocus = focus || scope.querySelector('[data-rc-mc-close]');
      }).then(() => {
        if (restoreFocus && this.restoreFocus && this.restoreFocus.isConnected && (document.activeElement === document.body || this.root.contains(document.activeElement))) this.restoreFocus.focus({ preventScroll: true });
        this.restoreFocus = null;
      });
    }

    render(html) {
      if (typeof html !== 'string') throw new Error('Your cart updated, but its display could not refresh. Please review your cart.');
      const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rc-mini-cart]');
      if (!parsed || !parsed.querySelector('[data-rc-mc-content]')) throw new Error('Your cart display could not refresh. Please review your cart.');
      const closeHadFocus = this.root.contains(document.activeElement) && document.activeElement.hasAttribute('data-rc-mc-close');
      const scrollTop = this.content.querySelector('.rc-mc-items').scrollTop;
      const drawerScroll = this.drawerContent.querySelector('.rc-mc-items')?.scrollTop || 0;
      this.content.innerHTML = parsed.querySelector('[data-rc-mc-content]').innerHTML;
      this.content.querySelector('.rc-mc-items').scrollTop = scrollTop;
      this.root.dataset.count = parsed.dataset.count;
      this.syncFailed = false;
      this.showError('');
      if (this.dialog.open) {
        this.renderDrawer();
        this.drawerContent.querySelector('.rc-mc-items').scrollTop = drawerScroll;
      }
      this.setBusy();
      this.publishCount(Number(parsed.dataset.count));
      if (closeHadFocus) (this.dialog.open ? this.drawerContent : this.content).querySelector('[data-rc-mc-close]').focus({ preventScroll: true });
    }

    publishCount(count) {
      this.publishing = true;
      window.dispatchEvent(new CustomEvent('cartcount:update', { detail: { item_count: count } }));
      this.publishing = false;
    }

    showError(message) {
      this.root.querySelectorAll('[data-rc-mc-error]').forEach(element => {
        element.textContent = message;
        element.hidden = !message;
        if (message) {
          const link = document.createElement('a');
          link.href = this.cartUrl;
          link.textContent = ' View full cart';
          element.appendChild(link);
        }
      });
    }
  }

  const root = document.querySelector('[data-rc-mini-cart]');
  if (root) window.RoyalMiniCart = new RoyalMiniCart(root);
})();
